import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { StepTimeline } from '../components/reviews/StepTimeline';
import { ReviewerOutputPanel } from '../components/reviews/ReviewerOutputPanel';
import type { ReviewDebugData } from '../components/reviews/types';

/**
 * Steps the backend retry endpoint accepts. MUST stay in sync with
 * `SUPPORTED_RETRY_TARGETS` in review-service/src/routes/retry.ts —
 * only free (non-LLM) steps with true checkpoint resume semantics qualify.
 * Offering retry for other steps (verify-session, fetch-enriched-context,
 * signal-round-N-start) would 400 on the backend and violate the
 * cost-control contract.
 * Hoisted to module scope to avoid re-allocating a Set on every render.
 */
const RETRYABLE_STEPS: ReadonlySet<string> = new Set([
  'post-findings',
  'signal-completion',
]);

/**
 * ReviewDebug — Spec 188 Phase 4.
 * Renders a full workflow timeline + per-reviewer cost breakdown for a single
 * review session. Supports retrying failed free steps inline.
 */
export function ReviewDebug() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [data, setData] = useState<ReviewDebugData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    async function load() {
      try {
        const result = await api.reviews.debug(sessionId!);
        if (!active) return;
        setData(result);
        setError(null);
      } catch (err) {
        if (!active) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('No checkpoint found for this review — it may have failed before the first round completed.');
        } else {
          setError(`Failed to load debug data: ${err instanceof Error ? err.message : String(err)}`);
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [sessionId]);

  const canRetryStep = useCallback((name: string): boolean => {
    return RETRYABLE_STEPS.has(name);
  }, []);

  const handleRetry = useCallback(async (stepName: string) => {
    if (!sessionId) return;
    try {
      await api.reviews.retry(sessionId, stepName);
      setRetryMessage(`Retry launched from ${stepName}. Refresh in ~30s to see the new timeline.`);
    } catch (err) {
      setRetryMessage(`Retry failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/reviews')}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white"
            aria-label="Back to reviews"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Review Debug</h1>
            {data && (
              <p className="text-xs text-gray-400 font-mono">
                {data.repo} · PR #{data.prNumber} · epoch {data.epoch} · {data.status}
              </p>
            )}
          </div>
        </div>

        {loading && <div data-testid="debug-loading">Loading debug data…</div>}

        {error && (
          <div
            data-testid="debug-error"
            className="mb-4 rounded-lg border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-300"
          >
            {error}
          </div>
        )}

        {retryMessage && (
          <div
            data-testid="debug-retry-message"
            className="mb-4 rounded-lg border border-yellow-800 bg-yellow-900/30 px-4 py-3 text-sm text-yellow-300"
          >
            {retryMessage}
          </div>
        )}

        {data && !loading && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <StepTimeline
              steps={data.steps}
              canRetry={canRetryStep}
              onRetry={handleRetry}
            />
            <ReviewerOutputPanel
              totalCostUsd={data.totalCostUsd}
              costByRound={data.costByRound}
              steps={data.steps}
            />
          </div>
        )}
      </div>
    </div>
  );
}
